import type { TenantPublicConfig } from '@/types/TenantConfig'

/**
 * Brand-token interpolation for i18n messages (cutover blocker D1, FE half).
 *
 * i18n_messages (tenant DB, seeded by agency-schema V3) embed literal {{agencyName}} /
 * {{agencyEmail}} placeholders. vue-i18n's message compiler REJECTS the `{{ }}` syntax
 * ("Not allowed nest placeholder") and renders the source verbatim — so postTranslation
 * never runs and users see raw tokens. We therefore resolve tokens in the message *source*
 * before it reaches the compiler, at the single funnel every runtime message passes through:
 * `setLocaleMessage`.
 *
 * Mirrors agency-backend `NotificationBrandTokenResolver`: known brand tokens are ALWAYS
 * substituted (never the literal `{{...}}`, never the platform name "TAP"); unknown tokens
 * (e.g. {{year}} owned by the tenant store, SpEL {{var}}) are left untouched. Fallback chain
 * matches the backend exactly.
 */

/** The brand tokens this layer owns. Anything else is left verbatim for other engines. */
const BRAND_TOKEN_KEYS = ['agencyName', 'agencyEmail', 'appName', 'brandName'] as const
type BrandTokenKey = (typeof BRAND_TOKEN_KEYS)[number]
export type BrandValues = Record<BrandTokenKey, string>

const TOKEN_RE = /\{\{(\w+)\}\}/g

/** Backend `firstNonBlank`: first value that is non-null and not blank, returned as-is; else "". */
function firstNonBlank(...values: Array<string | undefined | null>): string {
    for (const v of values) {
        if (v != null && v.trim() !== '') return v
    }
    return ''
}

/**
 * Derive brand values from the tenant's public config, mirroring the backend resolver's
 * fallback semantics. NEVER returns the literal token and NEVER "TAP" — a missing brand
 * degrades to "" (empty string), exactly like the backend.
 */
export function computeBrandValues(config?: TenantPublicConfig | null): BrandValues {
    const appName = config?.identity?.appName
    const brandName = config?.identity?.brandName
    const support = config?.urls?.support
    return {
        // agencyName -> appName -> brandName -> "" (identity displayName/appName first)
        agencyName: firstNonBlank(appName, brandName),
        appName: firstNonBlank(appName, brandName),
        brandName: firstNonBlank(brandName, appName),
        // agencyEmail -> support/contact email if available, else ""
        agencyEmail: firstNonBlank(support),
    }
}

/**
 * Replace known brand {{token}}s in a single string. Known token -> its value (even ""),
 * unknown token -> left verbatim so other engines can still resolve it. Pure & testable.
 */
export function resolveBrandTokens(text: string, values: BrandValues): string {
    if (!text || text.indexOf('{{') === -1) return text
    return text.replace(TOKEN_RE, (match, token: string) =>
        Object.prototype.hasOwnProperty.call(values, token)
            ? values[token as BrandTokenKey]
            : match,
    )
}

/** Deep-resolve brand tokens across a whole (nested) locale-message tree, non-destructively. */
export function resolveMessageTree<T>(node: T, values: BrandValues): T {
    if (typeof node === 'string') return resolveBrandTokens(node, values) as unknown as T
    if (Array.isArray(node)) return node.map((n) => resolveMessageTree(n, values)) as unknown as T
    if (node && typeof node === 'object') {
        const out: Record<string, unknown> = {}
        for (const key of Object.keys(node as Record<string, unknown>)) {
            out[key] = resolveMessageTree((node as Record<string, unknown>)[key], values)
        }
        return out as unknown as T
    }
    return node
}

// ─── Runtime installation ────────────────────────────────────────────────────

/**
 * Pre-branding placeholder: the tenant display name from edge-injected bootstrap data
 * (window.__TENANT__) if present, else "" — never the literal token, never "TAP".
 */
function bootstrapConfig(): TenantPublicConfig | null {
    if (typeof window === 'undefined') return null
    return (window as unknown as { __TENANT__?: TenantPublicConfig }).__TENANT__ ?? null
}

let currentValues: BrandValues = computeBrandValues(bootstrapConfig())

/** Token-bearing message source per locale (the truth we re-resolve from when branding loads). */
const rawByLocale = new Map<string, unknown>()

type Composer = {
    setLocaleMessage: (locale: string, message: unknown) => void
    getLocaleMessage: (locale: string) => unknown
}
let composer: Composer | null = null
let origSetLocaleMessage: Composer['setLocaleMessage'] | null = null

/**
 * Single chokepoint: wrap `setLocaleMessage` so every message installed (backend i18n sync,
 * cached translations, any future writer) is brand-resolved before the compiler sees it, and
 * its token-bearing source is retained so a late-arriving branding can re-resolve. `getLocaleMessage`
 * is wrapped to hand back that token-bearing source, keeping I18nSyncService's read-modify-write
 * lossless (it merges onto the raw source, not the already-resolved copy).
 */
export function installBrandTokens(i18n: { global: unknown }): void {
    const g = i18n.global as Composer
    composer = g
    const origSet = g.setLocaleMessage.bind(g)
    const origGet = g.getLocaleMessage.bind(g)
    origSetLocaleMessage = origSet

    g.setLocaleMessage = (locale: string, message: unknown) => {
        rawByLocale.set(locale, JSON.parse(JSON.stringify(message ?? {})))
        return origSet(locale, resolveMessageTree(message, currentValues))
    }
    g.getLocaleMessage = ((locale: string) =>
        rawByLocale.has(locale) ? rawByLocale.get(locale) : origGet(locale)) as Composer['getLocaleMessage']
}

/** Re-resolve every loaded locale from its token-bearing source using the latest brand values. */
export function refreshBrandTokens(): void {
    if (!composer || !origSetLocaleMessage) return
    for (const [locale, raw] of rawByLocale) {
        origSetLocaleMessage.call(composer, locale, resolveMessageTree(raw, currentValues))
    }
}

/**
 * Publish new brand values (called by the tenant store when its public config loads or is
 * enriched post-login) and re-resolve all loaded messages so users never keep seeing raw
 * tokens after branding arrives.
 */
export function applyBrandValues(values: BrandValues): void {
    currentValues = values
    refreshBrandTokens()
}

/** Current brand values — exposed for tests/diagnostics. */
export function getBrandValues(): BrandValues {
    return currentValues
}
